import * as React from "react";
import { useState } from "react";
import styled, { css } from "styled-components";
import useHistory from "routing/useHistory";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { Spinner, IconButton, IfPermitted } from "components/common";
import { Row, Col } from "security-app/components/common/FormBaseStyles";
import { PaginationProvider, ModalProvider, usePagination, useSetPagination, useModalContext } from "common/contexts";
import useRightSidebar from "hooks/useRightSidebar";
import { useGetAsset } from "security-app/hooks/useAssetsAPI";
import { useGetSecurityEvents } from "security-app/hooks/useSecurityEventsAPI";
import { relativeDifferenceSeconds } from "util/DateTime";
import useUserDateTime from "hooks/useUserDateTime";
import VulnerabilitiesCount from "security-app/components/Assets/AssetDetails/VulnerabilitiesCount";
import SecurityEventsCount from "security-app/components/Assets/AssetDetails/SecurityEventsCount";
import SecurityRoutes from "security-app/routing/Routes";
import useAssetIdSearchParam from "security-app/hooks/useAssetIdSearchParam";
import MachineForm from "security-app/components/Assets/AssetModals/MachineForm";
import UserForm from "security-app/components/Assets/AssetModals/UserForm";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import Details from "../AssetModals/AssetDetailsDrawer/Details";
import SecurityEvents from "../AssetModals/AssetDetailsDrawer/SecurityEvents";
import Vulnerabilities from "../AssetModals/AssetDetailsDrawer/Vulnerabilities";
const NavTab = styled.span(
  ({ $active, theme }) => css`
    cursor: pointer;
    color: ${$active ? `${theme.colors.text.primary}` : "grey"};
    padding: 0.5rem;
    border-bottom: 1px solid ${$active ? `${theme.colors.button.primary.background}` : "transparent"};

    &:hover {
      color: ${theme.colors.text.primary};
    }
  `
);
const HeaderDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;
const rowGap = "0.25rem";
const TabLabel = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const VulnerabilitiesTabLabel = ({ vulnerabilities = [] }) => /* @__PURE__ */ React.createElement(Row, { $gap: rowGap }, /* @__PURE__ */ React.createElement(TabLabel, { title: "Vulnerabilities" }, "Vulnerabilities"), /* @__PURE__ */ React.createElement(VulnerabilitiesCount, { vulnerabilities }));
const SecurityEventsLabel = ({ events = null }) => /* @__PURE__ */ React.createElement(Row, { $gap: rowGap }, /* @__PURE__ */ React.createElement(TabLabel, { title: "Events" }, "Events"), /* @__PURE__ */ React.createElement(SecurityEventsCount, { events }));
const tabs = [
  {
    id: "details",
    Label: () => /* @__PURE__ */ React.createElement("span", null, "Details"),
    Component: Details,
    type: ["machine", "user"]
  },
  {
    id: "security_events",
    Label: SecurityEventsLabel,
    Component: SecurityEvents,
    type: ["machine", "user"]
  },
  {
    id: "vulnerabilities",
    Label: VulnerabilitiesTabLabel,
    Component: Vulnerabilities,
    type: ["machine"]
  }
];
const SidebarHeader = ({ asset }) => {
  const { setModal, setEntity } = useModalContext();
  const { saveAssetIdSearchParam } = useAssetIdSearchParam();
  const { push } = useHistory();
  const onEdit = () => {
    setEntity({ asset });
    setModal(asset.details.type === "machine" ? "MACHINE-FORM" : "USER-FORM");
  };
  const showAssetDashboard = () => {
    saveAssetIdSearchParam(asset.id);
    push(`${SecurityRoutes.ACTIVITY.ASSET_DRILLDOWN}?assetId=${asset.id}`);
  };
  return /* @__PURE__ */ React.createElement(HeaderDiv, null, /* @__PURE__ */ React.createElement("h3", null, asset.name), /* @__PURE__ */ React.createElement(Row, { $gap: "0.5rem" }, /* @__PURE__ */ React.createElement(IconButton, { title: "Show Asset Dashboard", iconType: "regular", name: "dashboard", onClick: showAssetDashboard }), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "asset:edit" }, /* @__PURE__ */ React.createElement(IconButton, { title: "Edit Asset", iconType: "regular", name: "edit_square", onClick: onEdit }))));
};
const SidebarFormModals = () => {
  const { modal, setModal, entity, setEntity } = useModalContext();
  const onClose = () => {
    setModal(null);
    setEntity(null);
  };
  switch (modal) {
    case "MACHINE-FORM":
      return /* @__PURE__ */ React.createElement(MachineForm, { asset: entity == null ? void 0 : entity.asset, show: true, onClose });
    case "USER-FORM":
      return /* @__PURE__ */ React.createElement(UserForm, { asset: entity == null ? void 0 : entity.asset, show: true, onClose });
    default:
      return null;
  }
};
function AssetSidebarInner({ asset }) {
  const [activeTab, setActiveTab] = useState(0);
  const localPagination = usePagination();
  const setLocalPagination = useSetPagination();
  const { userTimezone } = useUserDateTime();
  const { openSidebar } = useRightSidebar();
  const sendTelemetry = useSendTelemetry("asset");
  const onEventSelect = (eventId) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.SECURITY_APP.ASSET_SIDEBAR_EVENT_SELECTED, {
      app_pathname: "security",
      event_details: { event_id: eventId }
    });
    openSidebar({
      id: `security-event-${eventId}`,
      title: "Security Event Details",
      componentKey: "EventReplaySideBarDetails",
      props: { alertId: eventId }
    });
  };
  const { securityEvents, pagination } = useGetSecurityEvents(
    {
      ...localPagination,
      filters: {
        alerts: "include",
        timerange: relativeDifferenceSeconds(asset.created_at),
        associated_assets: [asset.id]
      }
    },
    userTimezone
  );
  const onPageChange = (newPage, newPageSize) => {
    setLocalPagination({ ...localPagination, page: newPage, perPage: newPageSize });
  };
  const { Component } = tabs[activeTab];
  const tabsForCurrentAssetType = tabs.filter((tab) => tab.type.includes(asset.details.type));
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(SidebarHeader, { asset }), /* @__PURE__ */ React.createElement(Col, null, /* @__PURE__ */ React.createElement(Row, { $gap: "0.1rem", $width: "100%" }, tabsForCurrentAssetType.map((tab, index) => /* @__PURE__ */ React.createElement(
    NavTab,
    {
      key: tab.id,
      $active: activeTab === index,
      onClick: () => {
        sendTelemetry(TELEMETRY_EVENT_TYPE.SECURITY_APP.ASSET_SIDEBAR_TAB_CHANGED, {
          app_pathname: "security",
          event_details: { tab_id: tab.id }
        });
        setActiveTab(index);
      },
      role: "tab",
      "aria-label": tab.id
    },
    /* @__PURE__ */ React.createElement(tab.Label, { vulnerabilities: asset.vulnerabilities, events: pagination.total })
  ))), /* @__PURE__ */ React.createElement(
    Component,
    {
      asset,
      securityEvents,
      pagination,
      onPageChange,
      onEventSelect
    }
  )), /* @__PURE__ */ React.createElement(SidebarFormModals, null));
}
function AssetSidebarContent({ assetId }) {
  const { asset, loadingAsset } = useGetAsset(assetId);
  if (loadingAsset || !asset) {
    return /* @__PURE__ */ React.createElement(Spinner, { text: "Loading asset details..." });
  }
  return /* @__PURE__ */ React.createElement(ModalProvider, null, /* @__PURE__ */ React.createElement(PaginationProvider, null, /* @__PURE__ */ React.createElement(AssetSidebarInner, { asset })));
}
export default AssetSidebarContent;
